import api, { apiV2 } from './api'
import { getCadastroConfig } from '@/config/cadastros'
import type {
  TipoCadastro,
  ValidacaoApiResponse,
  JobEnfileiradoResponse,
  JobStatusResponse,
  ItensFalhaResponse,
  ItemCorrecao,
  CorrecaoResponse,
  CategoriaProduto,
  CategoriasProdutoResultado
} from '@/types'

const CATEGORIAS_PAGE_SIZE = 1000

// Validar arquivo Excel
export const validarArquivo = async (
  tipo: TipoCadastro,
  arquivo: File
): Promise<ValidacaoApiResponse> => {
  const config = getCadastroConfig(tipo)

  const formData = new FormData()
  formData.append('arquivo', arquivo)

  const response = await api.post<ValidacaoApiResponse>(
    config.endpoint,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      // Aceitar status 400 como resposta válida (não lançar erro)
      validateStatus: (status) => status === 200 || status === 400
    }
  )

  return response.data
}

// Enfileira a importação (fluxo de fila assíncrono → HTTP 202 com jobId).
// O processamento real é feito por um worker; acompanhe via getJobStatus.
export const processarImportacao = async (
  tipo: TipoCadastro,
  arquivo: File,
  onUploadProgress?: (percent: number) => void
): Promise<JobEnfileiradoResponse> => {
  const config = getCadastroConfig(tipo)

  const formData = new FormData()
  formData.append('arquivo', arquivo)

  const response = await api.post<JobEnfileiradoResponse>(
    config.endpointProcessar,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onUploadProgress) {
          onUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total))
        }
      }
    }
  )

  return response.data
}

// Consulta status e progresso de um job de importação.
export const getJobStatus = async (jobId: string): Promise<JobStatusResponse> => {
  const response = await api.get<JobStatusResponse>(`/importacao/jobs/${jobId}`)
  return response.data
}

// Lista (paginado) os itens que falharam, com dados originais e erros.
// status: filtro CSV opcional (ex.: 'INVALIDO,DUPLICADO,ERRO').
export const getJobItensFalha = async (
  jobId: string,
  status?: string,
  page = 1,
  limit = 100
): Promise<ItensFalhaResponse> => {
  const response = await api.get<ItensFalhaResponse>(`/importacao/jobs/${jobId}/itens`, {
    params: { ...(status ? { status } : {}), page, limit }
  })
  return response.data
}

// Salva as correções dos itens (INVALIDO/ERRO) — devolve-os para reprocessamento.
export const salvarCorrecoes = async (
  jobId: string,
  itens: ItemCorrecao[]
): Promise<CorrecaoResponse> => {
  const response = await api.patch<CorrecaoResponse>(`/importacao/jobs/${jobId}/itens`, { itens })
  return response.data
}

// Re-enfileira o mesmo job para reprocessar os itens PENDENTE (corrigidos).
export const reprocessarJob = async (jobId: string): Promise<JobEnfileiradoResponse> => {
  const response = await api.post<JobEnfileiradoResponse>(`/importacao/jobs/${jobId}/reprocessar`)
  return response.data
}

// Retorna o job mais recente (não arquivado) do usuário, ou null se não houver.
// Usado para retomar o acompanhamento ao (re)carregar a tela.
export const getJobAtual = async (): Promise<JobStatusResponse | null> => {
  const response = await api.get<JobStatusResponse>('/importacao/jobs/atual', {
    validateStatus: (status) => status === 200 || status === 204
  })
  if (response.status === 204 || !response.data) return null
  return response.data
}

// Arquiva (dispensa) um job — deixa de ser retomado na tela.
export const arquivarJob = async (jobId: string): Promise<void> => {
  await api.post(`/importacao/jobs/${jobId}/arquivar`)
}

// Baixar template
export const getTemplateUrl = (tipo: TipoCadastro): string => {
  const config = getCadastroConfig(tipo)
  return `/templates/${config.templateFile}`
}

// Buscar categorias de produto (api v2). Retorna null se falhar — front decide o fallback.
export const buscarCategoriasProduto = async (): Promise<CategoriasProdutoResultado | null> => {
  try {
    const response = await apiV2.get('/sygecom/produtos/categorias', {
      params: { page: 1, size: CATEGORIAS_PAGE_SIZE }
    })

    const data = response.data?.data
    const itemsRaw: unknown[] = Array.isArray(data?.items) ? data.items : []

    const items: CategoriaProduto[] = itemsRaw
      .map((item) => {
        const obj = item as { codcat?: unknown; descricao?: unknown }
        const codcat = obj.codcat !== undefined && obj.codcat !== null ? String(obj.codcat).trim() : ''
        const descricao = typeof obj.descricao === 'string' ? obj.descricao.trim() : ''
        return { codcat, descricao }
      })
      .filter((c) => c.codcat.length > 0)

    const totalItems = typeof data?.totalItems === 'number' ? data.totalItems : items.length

    return {
      items,
      totalItems,
      truncado: totalItems > items.length
    }
  } catch {
    return null
  }
}

export default {
  validarArquivo,
  processarImportacao,
  getJobStatus,
  getJobItensFalha,
  salvarCorrecoes,
  reprocessarJob,
  getJobAtual,
  arquivarJob,
  getTemplateUrl,
  buscarCategoriasProduto
}
